// AccentLab.tsx — the shadowing studio (2026-08-27). Method: "Accent strategy — softening the
// accent before hiring season" (teacher research). The loop: ONE consistent model voice speaks a
// sentence → the user repeats immediately → replay both → ONE thing to fix → again. Prosody
// (stress/rhythm) leads; segmentals follow. Monthly fixed-passage benchmark = the progress proof.
// Mandarin runs on the same engine — tones ARE the melody layer.
//
// Honesty: the AI coach never hears audio (transcript + timing only — same fence as /feedback);
// the EAR comparison is the replay-both button, exactly the manual method. Recording stays on
// this device; only the dictation text + timings go out, and only when coaching is requested.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, Volume2, X, Play, Sparkles, Target, Flame, Trash2, Upload, CalendarCheck, Check } from "lucide-react";
import { store, type AccentGoal, type Benchmark, type PracticePlan } from "./store";
import { speakExaminer, ttsAvailable, type SpeakHandle } from "./speech";
import { speakAI } from "./aiVoice";
import { repeatScore } from "./listen-repeat";
import { getShadowFeedback, draftPracticePlan, BrainError, type ShadowFeedback, type PlanSession } from "./brain";
import {
  L1_PROFILES, PRESENTER_SENTENCES, ZH_SURVIVAL, ZH_TONE_DRILLS,
  benchmarkPassageFor, monthsRemaining, dailyStreak, localDay, type AccentLang,
} from "./accent";

const getSR = (): any => (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

// Char-level similarity for hanzi (word-Levenshtein is meaningless without spaces).
function zhScore(target: string, heard: string): number {
  const norm = (s: string) => s.replace(/[\s，。？！?!,.·、；;:：""'']/g, "");
  const a = [...norm(target)], b = [...norm(heard)];
  if (!a.length) return 0;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return Math.max(0, Math.round(100 * (1 - dp[a.length][b.length] / a.length)));
}

type Tab = "drill" | "plan" | "benchmark";

interface DrillPick {
  key: string;
  title: string;
  why: string;
  items: { text: string; pinyin?: string; gloss?: string }[];
}

function drillsFor(lang: AccentLang): DrillPick[] {
  if (lang === "zh") {
    return [
      { key: "zh-tones", title: "The four tones", why: "Tones are the melody — a wrong tone is a different word. Dictation hearing a different character than the target IS the tone error, made visible.", items: ZH_TONE_DRILLS.map((p) => ({ text: p.hanzi, pinyin: p.pinyin, gloss: p.gloss })) },
      { key: "zh-survival", title: "Survival phrases", why: "The daily-life sentences you'll actually say this year. Copy the melody first, the sounds follow.", items: ZH_SURVIVAL.map((p) => ({ text: p.hanzi, pinyin: p.pinyin, gloss: p.gloss })) },
    ];
  }
  const ro = L1_PROFILES.find((p) => p.targetLang === "en");
  const out: DrillPick[] = (ro?.drills ?? []).map((d) => ({ key: d.key, title: d.title, why: d.why, items: d.sentences.map((t) => ({ text: t })) }));
  out.push({ key: "en-presenter", title: "Presenter shadowing", why: "Longer arcs in the register you're aiming for — copy the melody more than the words, exaggerate the stress at first.", items: PRESENTER_SENTENCES.map((t) => ({ text: t })) });
  return out;
}

export default function AccentLab({ onExit }: { onExit: () => void }) {
  const [lang, setLang] = useState<AccentLang>(() => {
    try { return localStorage.getItem("accent.lang") === "zh" ? "zh" : "en"; } catch { return "en"; }
  });
  useEffect(() => { try { localStorage.setItem("accent.lang", lang); } catch { /* ignore */ } }, [lang]);
  const [tab, setTab] = useState<Tab>("drill");
  const [goals, setGoals] = useState<AccentGoal[]>([]);
  const [streak, setStreak] = useState(0);

  const reloadGoals = useCallback(async () => {
    try { setGoals(await store.listAccentGoals()); } catch { /* fresh db */ }
    try {
      const attempts = await store.listShadowAttempts(400);
      setStreak(dailyStreak(new Set(attempts.map((a) => localDay(a.created_at)))));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { void reloadGoals(); }, [reloadGoals]);

  const [plan, setPlan] = useState<{ name: string; sessions: PlanSession[] } | null>(null);
  const [jump, setJump] = useState<{ key: string; nonce: number } | null>(null);
  const reloadPlan = useCallback(async () => {
    try {
      const row = await store.getPracticePlan(lang);
      if (row) { const p = JSON.parse(row.plan_json); setPlan({ name: row.name, sessions: p.sessions ?? [] }); }
      else setPlan(null);
    } catch { setPlan(null); }
  }, [lang]);
  useEffect(() => { void reloadPlan(); }, [reloadPlan]);

  const savePlan = useCallback(async (p: { name: string; sessions: PlanSession[] }) => {
    await store.savePracticePlan(lang, p.name, JSON.stringify({ sessions: p.sessions }));
    setPlan(p);
  }, [lang]);

  const nextSession = plan?.sessions.find((x) => !x.done_at) ?? null;

  const startSession = (sess: PlanSession) => {
    const first = sess.drill_keys[0];
    if (first === "benchmark") { setTab("benchmark"); return; }
    setJump({ key: first, nonce: (jump?.nonce ?? 0) + 1 });
    setTab("drill");
  };

  const markDone = useCallback(async (sess: PlanSession) => {
    if (!plan) return;
    const updated = { ...plan, sessions: plan.sessions.map((x) => x.idx === sess.idx ? { ...x, done_at: new Date().toISOString() } : x) };
    await savePlan(updated);
    // A finished session IS practice: it keeps the streak alive and feeds the next plan draft,
    // even when the drilling happened away from the recorder (out loud on a walk counts).
    try {
      await store.saveShadowAttempt({ lang, drillKey: sess.drill_keys[0] ?? "session", sentence: `[plan session done] ${sess.title}`, heard: "", localScore: null, aiJson: "", audioRef: "" });
    } catch { /* best-effort */ }
    reloadGoals();
  }, [plan, savePlan, lang, reloadGoals]);

  return (
    <main style={{
      maxWidth: 760, margin: "0 auto",
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      <div className="room-top" style={{ marginBottom: "0.6rem" }}>
        <button style={{ background: "none", border: 0, color: "var(--ink-2)", cursor: "pointer", font: "inherit", padding: "0.3rem 0", display: "inline-flex", alignItems: "center", gap: 5 }} onClick={onExit}>
          <X size={14} /> Done
        </button>
        {streak > 0 && <span className="badge badge-make" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Flame size={12} /> {streak}-day streak</span>}
      </div>
      <h1 className="serif" style={{ fontSize: "1.65rem", margin: "0 0 0.35rem" }}>Accent Lab</h1>
      <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 0.9rem", lineHeight: 1.5 }}>
        Shadow one voice, sentence by sentence — melody first, sounds second. Twenty focused minutes a day beats an occasional hour.
      </p>

      <GoalsStrip goals={goals} onChanged={reloadGoals} />

      {nextSession && (
        <section className="card" style={{ padding: "0.7rem 0.95rem", marginTop: "0.6rem", borderLeft: "3px solid var(--ink-2)" }} aria-label="Today's session">
          <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <CalendarCheck size={14} style={{ color: "var(--ink-2)" }} />
            <span style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Up next · {nextSession.title}</span>
            <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{nextSession.minutes} min · session {nextSession.idx} of {plan?.sessions.length}</span>
            <span style={{ marginLeft: "auto" }} />
            <button className="btn btn-sm btn-pill btn-primary" onClick={() => startSession(nextSession)}><Play size={12} /> Start</button>
            <button className="btn btn-sm btn-pill" onClick={() => void markDone(nextSession)} title="Finished this session"><Check size={12} /> Done</button>
          </div>
          {nextSession.note && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 4 }}>{nextSession.note}</div>}
        </section>
      )}

      <div className="row" style={{ gap: "0.35rem", margin: "0.9rem 0" }}>
        <button className={`chip toggle${lang === "en" ? " selected" : ""}`} onClick={() => { setLang("en"); setTab("drill"); }}>English accent</button>
        <button className={`chip toggle${lang === "zh" ? " selected" : ""}`} onClick={() => { setLang("zh"); setTab("drill"); }}>中文 · Chinese</button>
        <span style={{ marginLeft: "auto" }} />
        <button className={`chip toggle${tab === "drill" ? " selected" : ""}`} onClick={() => setTab("drill")}>Drills</button>
        <button className={`chip toggle${tab === "plan" ? " selected" : ""}`} onClick={() => setTab("plan")}>Plan</button>
        <button className={`chip toggle${tab === "benchmark" ? " selected" : ""}`} onClick={() => setTab("benchmark")}>Monthly benchmark</button>
      </div>

      {tab === "drill" && <ShadowLoop lang={lang} onPracticed={reloadGoals} jump={jump} />}
      {tab === "plan" && <PlanPanel lang={lang} goals={goals} plan={plan} onSave={savePlan} onDelete={async () => { await store.deletePracticePlan(lang); setPlan(null); }} onStart={startSession} onMarkDone={markDone} />}
      {tab === "benchmark" && <BenchmarkPanel lang={lang} />}
    </main>
  );
}

// ── Goals ─────────────────────────────────────────────────────────────────────
function GoalsStrip({ goals, onChanged }: { goals: AccentGoal[]; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [date, setDate] = useState("");

  const seed = async (n: string, l: string, d: string) => {
    await store.createAccentGoal(n, l, d);
    onChanged();
  };

  return (
    <section className="card" style={{ padding: "0.8rem 0.95rem" }} aria-label="Your goals">
      <div className="row" style={{ gap: 6, alignItems: "center", marginBottom: goals.length ? "0.5rem" : 0 }}>
        <Target size={14} style={{ color: "var(--ink-2)" }} />
        <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>Goals</span>
        <button className="chip" style={{ marginLeft: "auto" }} onClick={() => setAdding((a) => !a)}>{adding ? "Cancel" : "+ Goal"}</button>
      </div>
      {goals.map((g) => {
        const months = g.target_date ? monthsRemaining(g.target_date) : null;
        return (
          <div key={g.id} className="row" style={{ gap: 8, alignItems: "baseline", padding: "0.2rem 0" }}>
            <span style={{ fontSize: "var(--fs-sm)" }}>{g.name}</span>
            {months != null && (
              <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>
                {months === 0 ? "target month is now" : `${months} month${months === 1 ? "" : "s"} left`} · {g.target_date}
              </span>
            )}
            <button title="Delete goal" style={{ marginLeft: "auto", background: "none", border: 0, cursor: "pointer", color: "var(--ink-3)" }}
              onClick={async () => { await store.deleteAccentGoal(g.id); onChanged(); }}><Trash2 size={13} /></button>
          </div>
        );
      })}
      {goals.length === 0 && !adding && (
        <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <button className="chip" onClick={() => void seed("Polish my English accent", "en", "2027-11-01")}>Polish my English accent · Nov 2027</button>
          <button className="chip" onClick={() => void seed("Learn Chinese", "zh", "")}>Learn Chinese</button>
        </div>
      )}
      {adding && (
        <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Goal name" style={{ flex: 1, minWidth: 160 }} />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: "auto" }} />
          <button className="btn btn-sm" disabled={!name.trim()} onClick={async () => {
            await store.createAccentGoal(name.trim(), "en", date);
            setName(""); setDate(""); setAdding(false); onChanged();
          }}>Add</button>
        </div>
      )}
    </section>
  );
}

// ── The shadowing loop ────────────────────────────────────────────────────────
function ShadowLoop({ lang, onPracticed, jump }: { lang: AccentLang; onPracticed: () => void; jump?: { key: string; nonce: number } | null }) {
  const drills = useMemo(() => drillsFor(lang), [lang]);
  const [drillKey, setDrillKey] = useState(drills[0]?.key ?? "");
  useEffect(() => { setDrillKey(drills[0]?.key ?? ""); }, [drills]);
  // A plan's "Start" jumps straight into that session's first drill.
  const jumpNonce = jump?.nonce ?? 0;
  useEffect(() => {
    if (jump && drills.some((d) => d.key === jump.key)) setDrillKey(jump.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpNonce]);
  const drill = drills.find((d) => d.key === drillKey) ?? drills[0];

  // "Continue where it left off": the position in each drill survives leaving and coming back.
  const posKey = `accent.pos.${lang}.${drillKey}`;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    let saved = 0;
    try { saved = Number(localStorage.getItem(posKey)) || 0; } catch { /* fresh */ }
    setIdx(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillKey]);
  useEffect(() => { try { localStorage.setItem(posKey, String(idx)); } catch { /* ignore */ } }, [idx, posKey]);
  const item = drill?.items[idx % Math.max(1, drill.items.length)];

  const [speaking, setSpeaking] = useState(false);
  const [modelMs, setModelMs] = useState(0);
  const speakStart = useRef(0);
  const handleRef = useRef<SpeakHandle | null>(null);
  useEffect(() => () => { handleRef.current?.cancel(); }, []);

  const [recState, setRecState] = useState<"idle" | "recording" | "done">("idle");
  const [heard, setHeard] = useState("");
  const [myAudio, setMyAudio] = useState<string | null>(null); // object URL of the last take
  const [speakerMs, setSpeakerMs] = useState(0);
  const [localScore, setLocalScore] = useState<number | null>(null);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const recRef = useRef<any>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recStart = useRef(0);

  const [coach, setCoach] = useState<ShadowFeedback | null>(null);
  const [coachBusy, setCoachBusy] = useState(false);
  const [coachErr, setCoachErr] = useState<string | null>(null);

  const play = useCallback(() => {
    if (!item) return;
    handleRef.current?.cancel();
    speakStart.current = Date.now();
    const cbs = {
      onStart: () => setSpeaking(true),
      onEnd: () => { setSpeaking(false); setModelMs(Date.now() - speakStart.current); },
    };
    // The MODEL VOICE is the AI coach's voice — one consistent high-quality voice for every
    // language on every device (billed on a sentence's FIRST play; cached after, replays free).
    // The device's own TTS is only the silent fallback (offline / signed out / no funds).
    void (async () => {
      const spoke = await speakAI(item.text, lang === "zh" ? "Chinese (Mandarin)" : "English", cbs).catch(() => false);
      if (!spoke) handleRef.current = speakExaminer(item.text, cbs, { lang });
    })();
  }, [item, lang]);

  const startRecording = useCallback(async () => {
    setCoach(null); setCoachErr(null); setHeard(""); setLocalScore(null);
    if (myAudio) { URL.revokeObjectURL(myAudio); setMyAudio(null); }
    chunksRef.current = [];
    recStart.current = Date.now();
    // Best-effort audio capture for replay-both; dictation runs alongside. Either may be
    // unavailable — the loop still works with whichever succeeded.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (chunksRef.current.length) setMyAudio(URL.createObjectURL(new Blob(chunksRef.current, { type: chunksRef.current[0].type || "audio/webm" })));
      };
      mediaRef.current = mr;
      mr.start();
    } catch { mediaRef.current = null; }
    const SR = getSR();
    if (SR) {
      try {
        const rec = new SR();
        rec.lang = lang === "zh" ? "zh-CN" : "en-GB";
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e: any) => {
          const t = e?.results?.[0]?.[0]?.transcript;
          if (t) setHeard(String(t));
        };
        recRef.current = rec;
        rec.start();
      } catch { recRef.current = null; }
    }
    setRecState("recording");
  }, [lang, myAudio]);

  const stopRecording = useCallback(() => {
    setSpeakerMs(Date.now() - recStart.current);
    try { mediaRef.current?.stop(); } catch { /* ignore */ }
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setRecState("done");
  }, []);

  // Score once dictation lands (it arrives async after stop).
  useEffect(() => {
    if (recState !== "done" || !item) return;
    const sc = heard ? (lang === "zh" ? zhScore(item.text, heard) : repeatScore(item.text, heard)) : null;
    setLocalScore(sc);
    void store.saveShadowAttempt({
      lang, drillKey: drill?.key ?? "", sentence: item.text, heard,
      localScore: sc, aiJson: "", audioRef: "",
    }).then(() => onPracticed()).catch(() => { /* local save best-effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recState, heard]);

  const askCoach = useCallback(async () => {
    if (!item) return;
    setCoachBusy(true); setCoachErr(null);
    try {
      const fb = await getShadowFeedback({
        sentence: item.pinyin ? `${item.text} (${item.pinyin})` : item.text,
        heard, l1: "Romanian", lang, modelMs, speakerMs,
      });
      setCoach(fb);
    } catch (e) {
      setCoachErr(e instanceof BrainError ? e.message : "Coaching didn't come back — try again.");
    } finally { setCoachBusy(false); }
  }, [item, heard, lang, modelMs, speakerMs]);

  const next = () => {
    handleRef.current?.cancel();
    setRecState("idle"); setHeard(""); setLocalScore(null); setCoach(null); setCoachErr(null);
    if (myAudio) { URL.revokeObjectURL(myAudio); setMyAudio(null); }
    setIdx((i) => i + 1);
  };

  if (!drill || !item) return null;

  return (
    <div>
      <div className="row" style={{ gap: "0.3rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
        {drills.map((d) => (
          <button key={d.key} className={`chip toggle${drillKey === d.key ? " selected" : ""}`} onClick={() => setDrillKey(d.key)}>{d.title}</button>
        ))}
      </div>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, margin: "0 0 0.75rem" }}>{drill.why}</p>

      <section className="card" style={{ padding: "1rem 1.1rem", textAlign: "center" }}>
        <div className="serif" style={{ fontSize: lang === "zh" ? "1.5rem" : "1.15rem", lineHeight: 1.5, marginBottom: item.pinyin ? 2 : 8 }}>{item.text}</div>
        {item.pinyin && <div className="muted" style={{ fontSize: "var(--fs-sm)", marginBottom: 2 }}>{item.pinyin}</div>}
        {item.gloss && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 8 }}>{item.gloss}</div>}

        <div className="row" style={{ gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
          <button className="btn btn-pill" onClick={play} disabled={speaking || !ttsAvailable()}>
            <Volume2 size={14} /> {speaking ? "Speaking…" : "Model voice"}
          </button>

          {recState !== "recording" ? (
            <button className="btn btn-pill btn-primary" onClick={() => void startRecording()}>
              <Mic size={14} /> {recState === "done" ? "Again" : "Repeat it"}
            </button>
          ) : (
            <button className="btn btn-pill btn-primary" onClick={stopRecording}>
              <Square size={14} /> Stop
            </button>
          )}
          {myAudio && recState === "done" && (
            <button className="btn btn-pill" onClick={() => { void new Audio(myAudio).play(); }}>
              <Play size={14} /> Play me back
            </button>
          )}
          <button className="btn btn-pill" onClick={next}>Next →</button>
        </div>

        {recState === "done" && (
          <div style={{ marginTop: "0.9rem", textAlign: "left" }}>
            {heard
              ? <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>Dictation heard: “{heard}”{localScore != null ? ` · ${localScore}% match` : ""}</div>
              : <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>Dictation didn't catch it — use “Play me back” and your ear, or try again a touch louder.</div>}
            {modelMs > 0 && speakerMs > 0 && (
              <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>
                Timing: model {(modelMs / 1000).toFixed(1)}s · you {(speakerMs / 1000).toFixed(1)}s
                {speakerMs > modelMs * 1.35 ? " — likely evening out the rhythm; crush the weak syllables." : ""}
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <button className="btn btn-sm btn-pill" onClick={() => void askCoach()} disabled={coachBusy}>
                <Sparkles size={13} /> {coachBusy ? "Coaching…" : "Coach this attempt"}
              </button>
            </div>
            {coachErr && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>{coachErr}</div>}
            {coach && (
              <div className="card" style={{ marginTop: 8, padding: "0.7rem 0.85rem", textAlign: "left" }}>
                <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: 4 }}>One thing: {coach.one_thing}</div>
                {coach.prosody && <div style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, marginBottom: 4 }}><strong>Melody</strong> — {coach.prosody}</div>}
                {coach.segmentals && <div style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, marginBottom: 4 }}><strong>Sounds</strong> — {coach.segmentals}</div>}
                {coach.praise && <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{coach.praise}</div>}
              </div>
            )}
          </div>
        )}
      </section>
      <p className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 8, lineHeight: 1.5 }}>
        Your drill audio is held in memory for replay and discarded when you leave — Oriora never receives it, and it is not saved, so a backup cannot carry it. {/* §7b-verified — this component's MediaRecorder blob goes to URL.createObjectURL only (never store.*), so nothing persists for backup.rs to export. The monthly benchmark clips ARE saved and DO ride a backup; that is disclosed in the benchmark section, not here. */}
        The words are read by your device's own dictation. The model voice is the AI coach speaking (a sentence's first play is billed, replays come from the cache; offline it falls back to your device's voice). Coaching sends only the target sentence, the dictation text and the timings — billed like any AI action.
      </p>
    </div>
  );
}

// ── The practice plan — AI drafts, you review and edit, nothing saves without you ──
function PlanPanel({ lang, goals, plan, onSave, onDelete, onStart, onMarkDone }: {
  lang: AccentLang;
  goals: AccentGoal[];
  plan: { name: string; sessions: PlanSession[] } | null;
  onSave: (p: { name: string; sessions: PlanSession[] }) => Promise<void>;
  onDelete: () => Promise<void>;
  onStart: (s: PlanSession) => void;
  onMarkDone: (s: PlanSession) => Promise<void>;
}) {
  const [draft, setDraft] = useState<{ name: string; sessions: PlanSession[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [minutes, setMinutes] = useState(25);
  const [ownWords, setOwnWords] = useState("");

  const goal = goals.find((g) => g.lang === lang) ?? goals[0] ?? null;
  const catalog = useMemo(() => drillsFor(lang).map((d) => ({ key: d.key, title: d.title })), [lang]);

  const doDraft = async () => {
    setBusy(true); setErr(null);
    try {
      const [days, attempts, benchRows] = await Promise.all([
        store.listShadowDays().catch(() => [] as string[]),
        store.listShadowAttempts(300).catch(() => []),
        store.listBenchmarks().catch(() => []),
      ]);
      const cutoff = new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10);
      const byDrill: Record<string, number> = {};
      for (const a of attempts) if (a.lang === lang) byDrill[a.drill_key] = (byDrill[a.drill_key] ?? 0) + 1;
      const p = await draftPracticePlan({
        goal: {
          name: goal?.name ?? (lang === "zh" ? "Learn Chinese" : "Polish my accent"),
          lang,
          target_date: goal?.target_date ?? "",
          months_left: goal?.target_date ? monthsRemaining(goal.target_date) : null,
        },
        history: {
          days_practiced_last_14: days.filter((d) => d >= cutoff).length,
          attempts_by_drill: byDrill,
          benchmarks: benchRows.filter((b) => b.lang === lang).length,
        },
        minutesPerDay: minutes,
        catalog: [...catalog, { key: "benchmark", title: "Monthly benchmark recording" }],
        ownWords,
      });
      setDraft(p);
    } catch (e) {
      setErr(e instanceof BrainError ? e.message : "The draft didn't come back — try again.");
    } finally { setBusy(false); }
  };

  // ── Saved plan view ──
  if (plan && !draft) {
    const done = plan.sessions.filter((x) => x.done_at).length;
    const next = plan.sessions.find((x) => !x.done_at) ?? null;
    return (
      <div>
        <div className="row" style={{ gap: 8, alignItems: "baseline", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600 }}>{plan.name}</span>
          <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{done} of {plan.sessions.length} sessions done</span>
          <span style={{ marginLeft: "auto" }} />
          <button className="chip" onClick={() => { setDraft(null); void doDraft(); }} disabled={busy}>{busy ? "Drafting…" : "Re-draft"}</button>
          <button className="chip" onClick={() => void onDelete()}>Delete plan</button>
        </div>
        {err && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: 6 }}>{err}</div>}
        {plan.sessions.map((sess) => (
          <div key={sess.idx} className="row card" style={{ gap: 10, alignItems: "center", padding: "0.5rem 0.75rem", marginBottom: 6, opacity: sess.done_at ? 0.55 : 1, borderLeft: next?.idx === sess.idx ? "3px solid var(--ink-2)" : undefined }}>
            <span style={{ fontSize: "var(--fs-xs)", minWidth: 20, textAlign: "right" }}>{sess.idx}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: "var(--fs-sm)", display: "block" }}>{sess.done_at ? <s>{sess.title}</s> : sess.title}</span>
              <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>{sess.minutes} min · {sess.drill_keys.join(" · ")}</span>
            </span>
            {sess.done_at
              ? <Check size={14} style={{ color: "var(--ink-2)" }} />
              : (<>
                  <button className="btn btn-sm btn-pill" onClick={() => onStart(sess)}><Play size={12} /> Start</button>
                  <button className="btn btn-sm btn-pill" onClick={() => void onMarkDone(sess)}><Check size={12} /></button>
                </>)}
          </div>
        ))}
      </div>
    );
  }

  // ── Proposal review ──
  if (draft) {
    return (
      <div>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, margin: "0 0 0.6rem" }}>
          The proposal — remove anything that doesn't fit, then keep it. Nothing is saved until you do.
        </p>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{draft.name}</div>
        {draft.sessions.map((sess) => (
          <div key={sess.idx} className="row card" style={{ gap: 10, alignItems: "center", padding: "0.5rem 0.75rem", marginBottom: 6 }}>
            <span style={{ fontSize: "var(--fs-xs)", minWidth: 20, textAlign: "right" }}>{sess.idx}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: "var(--fs-sm)", display: "block" }}>{sess.title}</span>
              <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>{sess.minutes} min · {sess.drill_keys.join(" · ")}{sess.note ? ` — ${sess.note}` : ""}</span>
            </span>
            <button title="Remove this session" style={{ background: "none", border: 0, cursor: "pointer", color: "var(--ink-3)" }}
              onClick={() => setDraft({ ...draft, sessions: draft.sessions.filter((x) => x.idx !== sess.idx).map((x, i) => ({ ...x, idx: i + 1 })) })}><Trash2 size={13} /></button>
          </div>
        ))}
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <button className="btn btn-pill btn-primary" disabled={!draft.sessions.length} onClick={async () => { await onSave(draft); setDraft(null); }}>Keep this plan</button>
          <button className="btn btn-pill" onClick={() => setDraft(null)}>Discard</button>
        </div>
      </div>
    );
  }

  // ── No plan yet: the drafting form ──
  return (
    <div>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, margin: "0 0 0.6rem" }}>
        The AI drafts a 2–3-week practice track from your goal, your real practice history and the method
        (melody first, benchmark monthly). You review and edit it before anything is saved; each day the
        Lab shows the next session and picks up exactly where you left off.
      </p>
      <section className="card" style={{ padding: "0.9rem 1rem" }}>
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{ fontSize: "var(--fs-sm)" }}>Minutes a day</span>
          {[20, 25, 30].map((m) => (
            <button key={m} className={`chip toggle${minutes === m ? " selected" : ""}`} onClick={() => setMinutes(m)}>{m}</button>
          ))}
        </div>
        <textarea value={ownWords} onChange={(e) => setOwnWords(e.target.value)} rows={2}
          placeholder="Optional — your own words about how you want it shaped ('weekends heavier', 'tones every day', …)"
          style={{ width: "100%", font: "inherit", fontSize: "var(--fs-xs)", marginBottom: 8 }} />
        <button className="btn btn-pill btn-primary" onClick={() => void doDraft()} disabled={busy}>
          <Sparkles size={14} /> {busy ? "Drafting…" : "Draft my plan"}
        </button>
        {err && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>{err}</div>}
      </section>
    </div>
  );
}

// ── Monthly benchmark ─────────────────────────────────────────────────────────
function BenchmarkPanel({ lang }: { lang: AccentLang }) {
  const [rows, setRows] = useState<Benchmark[]>([]);
  const [passage, setPassage] = useState(benchmarkPassageFor(lang));
  useEffect(() => { setPassage(benchmarkPassageFor(lang)); }, [lang]);
  const [editing, setEditing] = useState(false);
  const [recState, setRecState] = useState<"idle" | "recording" | "saving">("idle");
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const mine = useMemo(() => rows.filter((r) => r.lang === lang), [rows, lang]);
  const locked = mine.length > 0; // §4.5: the passage never changes once the archive has begun

  const reload = useCallback(async () => {
    try {
      const all = await store.listBenchmarks();
      setRows(all);
      const first = all.find((r) => r.lang === lang);
      if (first) setPassage(first.passage);
    } catch { /* fresh db */ }
  }, [lang]);
  useEffect(() => { void reload(); }, [reload]);

  const saveClip = useCallback(async (blob: Blob) => {
    setRecState("saving");
    try {
      const dataUrl: string = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result));
        fr.onerror = () => rej(fr.error);
        fr.readAsDataURL(blob);
      });
      await store.saveBenchmark(lang, passage, dataUrl);
      await reload();
    } finally { setRecState("idle"); }
  }, [lang, passage, reload]);

  const start = useCallback(async () => {
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (chunksRef.current.length) void saveClip(new Blob(chunksRef.current, { type: chunksRef.current[0].type || "audio/webm" }));
      };
      mediaRef.current = mr;
      mr.start();
      setRecState("recording");
    } catch { /* mic unavailable */ }
  }, [saveClip]);

  return (
    <div>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, margin: "0 0 0.6rem" }}>
        Record the SAME passage on the 1st of every month. Ear-memory lies; the dated archive doesn't — play any two side by side and hear the distance travelled. These clips are saved on this device and no AI call ever reads them; a backup does carry them, through Oriora to your own storage, and Oriora keeps nothing.
      </p>
      <section className="card" style={{ padding: "0.9rem 1rem" }}>
        {editing && !locked ? (
          <div>
            <textarea value={passage} onChange={(e) => setPassage(e.target.value)} rows={6} style={{ width: "100%", font: "inherit", fontSize: "var(--fs-sm)" }} />
            <button className="btn btn-sm" style={{ marginTop: 6 }} onClick={() => setEditing(false)}>Keep this passage</button>
          </div>
        ) : (
          <p className="serif" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.65, margin: 0 }}>{passage}</p>
        )}
        <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {recState !== "recording" ? (
            <button className="btn btn-pill btn-primary" onClick={() => void start()} disabled={recState === "saving"}>
              <Mic size={14} /> {recState === "saving" ? "Saving…" : "Record this month's take"}
            </button>
          ) : (
            <button className="btn btn-pill btn-primary" onClick={() => { try { mediaRef.current?.stop(); } catch { /* ignore */ } }}>
              <Square size={14} /> Stop &amp; save
            </button>
          )}
          <button className="btn btn-pill" onClick={() => fileRef.current?.click()} title="Already recorded a baseline elsewhere? Bring it in.">
            <Upload size={14} /> Import a recording
          </button>
          <input ref={fileRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void saveClip(f);
            e.currentTarget.value = "";
          }} />
          {!locked && !editing && (
            <button className="chip" onClick={() => setEditing(true)}>Change the passage (locks after the first take)</button>
          )}
        </div>
      </section>

      {mine.length > 0 && (
        <section style={{ marginTop: "0.9rem" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: 6 }}>The archive · {mine.length} take{mine.length === 1 ? "" : "s"}</div>
          {mine.map((b) => (
            <div key={b.id} className="row card" style={{ gap: 10, alignItems: "center", padding: "0.5rem 0.75rem", marginBottom: 6 }}>
              <span style={{ fontSize: "var(--fs-xs)", minWidth: 84 }}>{b.recorded_on.slice(0, 10)}</span>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio controls src={b.audio_ref} style={{ height: 32, flex: 1 }} />
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
